import { useNavigate } from '@tanstack/react-router'
import { CornerDownLeft, KeyRound, Receipt, Search, UserRound, Building, Wrench } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Kbd } from '@/components/ui/kbd'
import { useMe } from '@/features/auth/api'
import { useUiStore } from '@/stores/ui'
import { filterNav, type NavItem, type NavSection } from '../nav'

const SETTINGS_ITEMS: NavItem[] = [
  { label: 'Organization settings', to: '/settings/organization', icon: Building },
  { label: 'Subscription & usage', to: '/settings/subscription', icon: Receipt, keywords: ['billing', 'plan', 'upgrade'] },
  { label: 'Profile', to: '/settings/profile', icon: UserRound },
  { label: 'Security', to: '/settings/security', icon: KeyRound, keywords: ['password', '2fa', 'sessions', 'tokens'] },
]

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function CommandPaletteButton() {
  const setOpen = useUiStore((s) => s.setCommandOpen)
  return (
    <Button
      variant="outline"
      onClick={() => setOpen(true)}
      className="h-9 w-9 justify-start gap-2 px-0 text-muted-foreground shadow-none max-sm:justify-center sm:w-56 sm:px-3 lg:w-72"
      aria-label="Search (Command K)"
    >
      <Search className="text-muted-foreground" />
      <span className="hidden flex-1 text-left font-normal sm:inline">Jump to…</span>
      <span className="hidden items-center gap-0.5 sm:flex">
        <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
        <Kbd>K</Kbd>
      </span>
    </Button>
  )
}

export function CommandPalette({ sections }: { sections: NavSection[] }) {
  const open = useUiStore((s) => s.commandOpen)
  const setOpen = useUiStore((s) => s.setCommandOpen)
  const navigate = useNavigate()
  const { data: me } = useMe()

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(!useUiStore.getState().commandOpen)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setOpen])

  const groups = useMemo(() => {
    const extra: NavSection[] = [{ label: 'Settings', items: SETTINGS_ITEMS }]
    if (me?.user.is_platform_admin) extra.push({ label: 'Platform', items: [{ label: 'Platform admin', to: '/admin', icon: Wrench }] })
    return [...filterNav(sections, me?.permissions, me?.subscription?.entitlements.features), ...extra]
  }, [sections, me])

  function go(to: NavItem['to']) {
    setOpen(false)
    void navigate({ to })
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages…" />
      <CommandList>
        <CommandEmpty>No matching pages.</CommandEmpty>
        {groups.map((group, i) => (
          <CommandGroup key={group.label ?? i} heading={group.label ?? 'General'}>
            {group.items.map((item) => (
              <CommandItem key={`${group.label}-${item.to}`} value={`${group.label ?? ''} ${item.label}`} keywords={item.keywords} onSelect={() => go(item.to)}>
                <item.icon />
                <span>{item.label}</span>
                {item.upcoming && <span className="ml-auto text-xs text-muted-foreground">Coming soon</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="flex items-center gap-3 border-t px-4 py-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span className="flex items-center gap-1">
          <Kbd>
            <CornerDownLeft className="size-3" />
          </Kbd>{' '}
          open
        </span>
        <span className="ml-auto flex items-center gap-1">
          <Kbd>esc</Kbd> close
        </span>
      </div>
    </CommandDialog>
  )
}
